import parse from "html-react-parser";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import {
  IeltsListeningUserAttemptQuery,
  IeltsReadingUserAttemptQuery,
  IeltsSkill,
  IeltsWritingUserAttemptQuery,
} from "@/__generated__/graphql";
import IeltsMultipleChoiceInput from "@/components/ielts/common/Inputs/MultipleChoiceInput";
import IeltsRadioInput from "@/components/ielts/common/Inputs/RadioInput";
import useIeltsReading from "@/components/ielts/reading/CheckPoint/DoExam/useIeltsReading";
import {
  activeListeningPartAtom,
  activeReadingPartAtom,
} from "@/store/ieltsListeningReading";
import {
  IeltsListeningReadingWritingFormValues,
  IeltsListeningReadingWritingQuestion,
} from "@/types";
import useIeltsListening from "../../../../listening/CheckPoint/DoExam/useIeltsListening";
import ExplainSelectDetail from "../../ExplainSelectQuestion";
import { configQuestionContent } from "../config";
import { ieltsWritingCurrentPartAtom } from "@/store/ieltsWriting";
import useDoIeltsWriting from "@/components/ielts/writing/CheckPoint/useDoIeltsWriting";
import useIeltsTheme from "../../../hooks/useIeltsTheme";
import { useAuth } from "@/providers/auth";

type Props = {
  question: IeltsListeningReadingWritingQuestion;
  isViewReport?: boolean;
  questionAttempt?: NonNullable<
    | NonNullable<
        IeltsListeningUserAttemptQuery["userAttempt"]
      >["ieltsListeningPartAttempts"]
    | NonNullable<
        IeltsReadingUserAttemptQuery["userAttempt"]
      >["ieltsReadingPartAttempts"]
    | NonNullable<
        IeltsWritingUserAttemptQuery["userAttempt"]
      >["ieltsWritingPartAttempts"]
  >[number]["questionAttempts"][number];
  type: IeltsSkill.Listening | IeltsSkill.Reading | IeltsSkill.Writing;
};

export default function IeltsListeningReadingSelectQuestion({
  question,
  isViewReport,
  questionAttempt,
  type,
}: Props) {
  const { watch } =
    useFormContext<IeltsListeningReadingWritingFormValues>() || {};
  const { theme } = useIeltsTheme();
  const { isACCADomain } = useAuth();

  const details = useMemo(() => question.node.details, [question.node.details]);

  const activeListeningPart = useAtomValue(activeListeningPartAtom);
  const activeReadingPart = useAtomValue(activeReadingPartAtom);
  const activeWritingPart = useAtomValue(ieltsWritingCurrentPartAtom);
  const activePart =
    type === IeltsSkill.Listening
      ? activeListeningPart
      : type === IeltsSkill.Writing
        ? activeWritingPart
        : activeReadingPart;
  const partQuestions =
    (type === IeltsSkill.Listening
      ? activeListeningPart?.node.ieltsListeningPartQuestions
      : type === IeltsSkill.Writing
        ? activeWritingPart?.node.ieltsWritingPartQuestions
        : activeReadingPart?.node.ieltsReadingPartQuestions) || [];
  const questionIndex = partQuestions.findIndex(
    (q) => q.node.id === question.node.id,
  );
  const partIndex = isViewReport
    ? 0
    : watch("parts")?.findIndex((p) => p.partId === activePart?.node.id);

  const { handleSubmit: listeningHandleSubmit } = useIeltsListening();
  const { handleSubmit: readingHandleSubmit } = useIeltsReading();
  const { handleSubmitPracticeIeltsPart } = useDoIeltsWriting();
  const handleSubmit =
    type === IeltsSkill.Listening
      ? listeningHandleSubmit
      : type === IeltsSkill.Writing
        ? handleSubmitPracticeIeltsPart
        : readingHandleSubmit;

  if (partIndex === -1) return null;

  return (
    <div>
      <p
        className="text-lg max-md:text-base font-bold mb-[5px]"
        style={{
          color: theme.colorPrimary,
        }}
      >
        {question.node.title}
      </p>
      <div className="mb-4 max-md:mb-2">
        {parse(question.node.contentHtml, {
          replace: (domNode) => {
            if (domNode.type === "tag" && domNode.name === "blank") {
              const detail = details.find((d) => d.id === domNode.attribs.id);
              if (!detail) return domNode;

              const isMultipleAnswer =
                (detail.options?.filter((o) => o.correctAnswer).length ?? 0) >
                1;

              const detailAttempt = questionAttempt?.detailAnswers.find(
                (d) => d.detailId === detail.id,
              );

              const detailAnswers = isViewReport
                ? []
                : watch(
                    `parts.${partIndex}.questions.${questionIndex}.detailAnswers`,
                  );

              const detailAnswer = isViewReport
                ? detailAttempt
                : (detailAnswers || []).find((d) => d.detailId === detail.id);

              const options =
                detail.options?.map((o) => {
                  const result = (() => {
                    if (!isViewReport) return;

                    if (detailAttempt?.optionIds?.includes(o.id)) {
                      return o.correctAnswer;
                    }
                  })();

                  return {
                    id: o.id,
                    label: o.label,
                    value: o.id,
                    title: o.text,
                    result,
                  };
                }) || [];

              if (isMultipleAnswer) {
                return (
                  <div className="mt-3 mb-6 max-md:mb-1 max-md:mt-2">
                    <IeltsMultipleChoiceInput
                      id={detail.id}
                      value={detailAnswer?.optionIds || []}
                      className="mb-2"
                      options={options}
                      readonly={isViewReport}
                      limit={
                        detail.options?.filter((o) => o.correctAnswer).length
                      }
                      onChange={(v) =>
                        handleSubmit({
                          questionId: question.node.id,
                          detailAnswer: {
                            detailId: detail.id,
                            optionIds: v,
                          },
                        })
                      }
                      useIndexLabel={isACCADomain}
                    />

                    {isViewReport && (
                      <ExplainSelectDetail detail={detail} showContentKey />
                    )}
                  </div>
                );
              }

              return (
                <div className="mt-3 mb-6 max-md:mb-1 max-md:mt-2">
                  <div className="relative w-fit">
                    <IeltsRadioInput
                      id={detail.id}
                      className="mb-2"
                      value={
                        detailAnswer?.optionIds?.[0] ||
                        detailAnswer?.optionId ||
                        ""
                      }
                      options={options}
                      readonly={isViewReport}
                      onChange={(v) =>
                        handleSubmit({
                          questionId: question.node.id,
                          detailAnswer: {
                            detailId: detail.id,
                            optionId: v,
                            optionIds: [v],
                          },
                        })
                      }
                      useIndexLabel={isACCADomain}
                    />
                    {/* {isViewReport && !detailAttempt?.optionIds?.includes(detail.id) && (
                      <IeltsInputResultIcon
                        className="absolute -top-[10px] -right-6"
                        result={false}
                      />
                    )} */}
                  </div>

                  {isViewReport && <ExplainSelectDetail detail={detail} />}
                </div>
              );
            }
            return configQuestionContent(domNode, theme);
          },
        })}
      </div>
    </div>
  );
}
